import { ChangeEvent, useMemo } from "react";
import { isStringTrue } from "@iddataweb/idw-common-ui/utils";
import { FormattedRpServiceConfigurationProperty } from "@idw-types/data/rp-service-configuration";
import { AvailablePropertyTypes } from "@enums/available-property-types";
import { SelectOption } from "@idw-types/common";
import { RpServiceConfigurationPropertyFormGroup } from "../rp-service-configuration-property-form-group/rp-service-configuration-property-form-group";
import { LabelWidthTypes } from "@enums/label-width-types";
import { faTrash } from "@fortawesome/free-solid-svg-icons/faTrash";
import { BOOLEAN_RADIO_INPUT_OPTIONS } from "@constants/boolean-radio-input-options";
import { LayoutTypes } from "@enums/layout-types";

type RpServiceConfigurationPropertyContainerProps = {
    isDisabled: boolean;
    layout?: LayoutTypes;
    idPrefix: string;
    rpServiceConfigurationProperty: FormattedRpServiceConfigurationProperty;
    removeRpServiceConfigurationProperty: (rpServiceConfigurationProperty: FormattedRpServiceConfigurationProperty) => void;
    setRpServiceConfigurationPropertyValue: (rpServiceConfigurationProperty: FormattedRpServiceConfigurationProperty, value: string) => void;
};

export const RpServiceConfigurationPropertyContainer = (props: RpServiceConfigurationPropertyContainerProps) => {
    const {
        isDisabled,
        layout,
        idPrefix,
        rpServiceConfigurationProperty,
        removeRpServiceConfigurationProperty,
        setRpServiceConfigurationPropertyValue
    } = props;

    const { selectOptions, selectedSelectOption } = useMemo(() => {
        let selectOptions: Array<SelectOption> = [];
        let selectedSelectOption = null;
        if (rpServiceConfigurationProperty.values && [AvailablePropertyTypes.SELECT].includes(rpServiceConfigurationProperty.type)) {
            try {
                const jsonValues: Array<SelectOption> = JSON.parse(rpServiceConfigurationProperty.values);
                selectOptions = jsonValues;
                selectedSelectOption = selectOptions?.find(option => option?.value === rpServiceConfigurationProperty.value) || null;
            } catch(error) {
                console.error(`There was an error parsing the values for available property ${rpServiceConfigurationProperty.propertyTypeId} named ${rpServiceConfigurationProperty.shortName}. Error: ${error}`);
            }
        } else if ([AvailablePropertyTypes.BOOLEAN].includes(rpServiceConfigurationProperty.type)) {
            selectOptions = BOOLEAN_RADIO_INPUT_OPTIONS;
            selectedSelectOption = isStringTrue(rpServiceConfigurationProperty.value) ? selectOptions[0] : selectOptions[1];
        }

        return {
            selectOptions: selectOptions,
            selectedSelectOption: selectedSelectOption,
        };
    }, [rpServiceConfigurationProperty]);

    const onChangeRadio = (newValue: string) => {
        const selectedRadioOption = selectOptions.find(so => so.value === newValue) || null;
        setRpServiceConfigurationPropertyValue(rpServiceConfigurationProperty, selectedRadioOption?.value);
    };

    const onChangeText = (event: ChangeEvent<HTMLInputElement>) => {
        setRpServiceConfigurationPropertyValue(rpServiceConfigurationProperty, event.target.value);
    };

    const onChangeSelect = (newValue: SelectOption) => {
        setRpServiceConfigurationPropertyValue(rpServiceConfigurationProperty, newValue?.value);
    };

    const onClickDelete = () => {
        removeRpServiceConfigurationProperty(rpServiceConfigurationProperty);
    };

    return (
        <RpServiceConfigurationPropertyFormGroup
            rpServiceConfigurationProperty={rpServiceConfigurationProperty}
            controlId={`${idPrefix}-property-${rpServiceConfigurationProperty.propertyId}-selected-property-${rpServiceConfigurationProperty.propertyTypeId}`}
            automationId={`${idPrefix}-property-${rpServiceConfigurationProperty.propertyId}-selected-property-${rpServiceConfigurationProperty.propertyTypeId}`}
            disabled={isDisabled}
            isRequired={true}
            label={rpServiceConfigurationProperty.displayName}
            labelWidth={LabelWidthTypes.LARGE}
            layout={layout}
            value={rpServiceConfigurationProperty.value}
            selectValue={selectedSelectOption}
            options={selectOptions}
            onChangeText={onChangeText}
            onChangeSelect={onChangeSelect}
            onChangeRadio={onChangeRadio}
            afterInputIcon={isDisabled ? null : faTrash}
            afterInputIconDisabled={isDisabled}
            afterInputIconOnClick={onClickDelete}
        />
    );
};